import type { Task } from "@medplum/fhirtypes";
import { and, eq, useLiveQuery } from "@tanstack/react-db";
import { useCollection } from "../core/hooks";
import type { ServiceRequestRow } from "../fhir/types";

const TASK_COLLECTION = "task";

/**
 * The work item standing for an order.
 *
 * ADDRESSED BY DISCRIMINATOR, not by `focus`. A procedure work item is minted
 * CLAIMED (`producer.ts`): `Task.focus` is left EMPTY and identity rides on a
 * `work-item-discriminator` identifier until evidence claims the item and the
 * anchor is stamped. Anything joining on `focus` therefore misses every item
 * that has not been charted yet — which is all of them, at the point anyone
 * wants to look one up.
 *
 * Kept OUT of `useOrderSet`: the procedures panel is rooted on orders
 * and must not gate on the queue. This is for the few places that genuinely
 * want the work item itself — routing to it, seeding an assistant with it.
 */
function findWorkItemIdByServiceRequestLocator(tasks: Iterable<Task>, serviceRequestId: string): string | undefined {
  const discriminator = `ServiceRequest/${serviceRequestId}`;
  for (const task of tasks) {
    const claimed = task.identifier?.some(
      (i) => i.system?.endsWith("/work-item-discriminator") && i.value === discriminator,
    );
    // `focus` still wins where it is set — an ANCHORED item, or a CLAIMED one
    // whose anchor was stamped when evidence arrived.
    const anchored = task.focus?.reference === discriminator;
    if (claimed || anchored) return task.id;
  }
  return undefined;
}

/**
 * Resolve the work item for one OrderSet member. Null while unresolved.
 *
 * BOTH ids are required, because a member id is PARENT-SCOPED, not global: every
 * OrderSet realized from the same protocol mints the same member ids (`exam-1`,
 * the occurrence id), so a patient with two visits from one protocol has two
 * orders answering to the same `orderId`. Resolving on the member id alone
 * returned a non-deterministic first row — sometimes the other visit's work item.
 */
export function useWorkItemIdForOrder(
  orderSetId: string | null | undefined,
  orderId: string | null | undefined,
): string | null {
  const tasks = useCollection(TASK_COLLECTION);
  const orders = useCollection("serviceRequest");
  const taskResult = useLiveQuery({
    query: (q) => {
      if (!orderId) return undefined;
      return q.from({ t: tasks });
    },
  });
  const orderResult = useLiveQuery({
    query: (q) => {
      if (!orderId || !orderSetId) return undefined;
      return q
        .from({ order: orders })
        .where(({ order }) => and(eq(order.orderSetId, orderSetId), eq(order.orderId, orderId)))
        .findOne();
    },
  });
  if (!orderId || !orderSetId) return null;
  // The work queue names an order by its FHIR locator, so the order's stored id is what it matches.
  const serviceRequestId = (orderResult.data as ServiceRequestRow | undefined)?.id;
  if (!serviceRequestId) return null;
  const rows = (taskResult.data as Task[] | undefined) ?? [];
  return findWorkItemIdByServiceRequestLocator(rows, serviceRequestId) ?? null;
}
